import { type Verdict } from './models/verdict'
import {
  type DelegationMessage,
  type DelegationResponse,
  type LoginMessage,
  type VerdictMessage,
} from './delegation'
import { GenericError } from './errors/GenericError'
import { getChromeRuntime } from './chrome/wrappers'

/**
 * Callbacks that will be called when receiving an action.
 */
export interface DelegationCallbacks {
  onIsLoggedIn: () => Promise<boolean>
  onLogin: (token: string) => Promise<void>
  onLogout: () => Promise<void>
  onIsDelegationActive: () => Promise<boolean>
  onStartDelegation: () => Promise<void>
  onStopDelegation: () => Promise<void>
  onSendVerdict: (
    requestId: string,
    safeSearch: boolean,
    verdict: Verdict
  ) => Promise<Verdict>
  onShouldIgnoreSafetyNet: () => Promise<boolean>
}

/**
 * Listens to delegation actions received from another extension.
 *
 * @param extensionIds - Identifiers of the extensions that can send delegation actions.
 * @param callbacks - Callback functions that should be called upon receiving an action.
 * @param onSetup - (Optional) Promise that should be called once the message listener is setup,
 * and before the first message is processed.
 */
export class DelegationListener {
  extensionIds: string[]
  callbacks: DelegationCallbacks
  isSetup = false

  constructor(
    extensionIds: string[],
    callbacks: DelegationCallbacks,
    onSetup?: () => Promise<void>
  ) {
    this.extensionIds = extensionIds
    this.callbacks = callbacks
    this.setupListeners(onSetup)
  }

  private readonly setupListeners = (onSetup?: () => Promise<void>): void => {
    getChromeRuntime().onMessageExternal.addListener(
      (request, sender, sendResponse) => {
        if (this.isExtensionAllowed(sender.id)) {
          void this.processAction(
            request as DelegationMessage,
            sendResponse,
            onSetup
          )
          return true
        }

        console.warn(
          `Extension ID ${sender.id ?? ''} is not allowed by the listener.`
        )
        this.sendErrorMessage(
          sendResponse,
          new GenericError(
            'EXTENSION_ID_NOT_ALLOWED',
            undefined,
            'Message was received but the extension ID is not allowed.'
          )
        )
        return false
      }
    )
  }

  private readonly isExtensionAllowed = (
    senderId: string | undefined
  ): boolean => {
    return senderId !== undefined && this.extensionIds.includes(senderId)
  }

  private readonly processAction = async (
    message: DelegationMessage,
    sendResponse: (response?: any) => void,
    onSetup?: () => Promise<void>
  ): Promise<void> => {
    // Run on first action
    if (onSetup !== undefined && !this.isSetup) {
      await onSetup()
      this.isSetup = true
    }

    switch (message.action) {
      case 'IS_LOGGED_IN':
        this.onIsLoggedIn(sendResponse)
        break
      case 'LOGIN':
        this.onLogin(sendResponse, message as LoginMessage)
        break
      case 'LOGOUT':
        this.onLogout(sendResponse)
        break
      case 'IS_DELEGATION_ACTIVE':
        this.onIsDelegationActive(sendResponse)
        break
      case 'START_DELEGATION':
        this.onStartDelegation(sendResponse)
        break
      case 'STOP_DELEGATION':
        this.onStopDelegation(sendResponse)
        break
      case 'SEND_VERDICT':
        this.onSendVerdict(sendResponse, message as VerdictMessage)
        break
      case 'SHOULD_IGNORE_SAFETY_NET':
        this.onShouldIgnoreSafetyNet(sendResponse)
        break
    }
  }

  private readonly onIsLoggedIn = (
    sendResponse: (response?: any) => void
  ): void => {
    this.runAction(sendResponse, this.callbacks.onIsLoggedIn())
  }

  private readonly onLogin = (
    sendResponse: (response?: any) => void,
    message: LoginMessage
  ): void => {
    this.runAction(sendResponse, this.callbacks.onLogin(message.token))
  }

  private readonly onLogout = (
    sendResponse: (response?: any) => void
  ): void => {
    this.runAction(sendResponse, this.callbacks.onLogout())
  }

  private readonly onIsDelegationActive = (
    sendResponse: (response?: any) => void
  ): void => {
    this.runAction(sendResponse, this.callbacks.onIsDelegationActive())
  }

  private readonly onStartDelegation = (
    sendResponse: (response?: any) => void
  ): void => {
    this.runAction(sendResponse, this.callbacks.onStartDelegation())
  }

  private readonly onStopDelegation = (
    sendResponse: (response?: any) => void
  ): void => {
    this.runAction(sendResponse, this.callbacks.onStopDelegation())
  }

  private readonly onSendVerdict = (
    sendResponse: (response?: any) => void,
    message: VerdictMessage
  ): void => {
    this.runAction(
      sendResponse,
      this.callbacks.onSendVerdict(
        message.requestId,
        message.safeSearch,
        message.verdict
      )
    )
  }

  private readonly onShouldIgnoreSafetyNet = (
    sendResponse: (response?: any) => void
  ): void => {
    this.runAction(sendResponse, this.callbacks.onShouldIgnoreSafetyNet())
  }

  /**
   * Run the callback promise and sends back the response.
   */
  private readonly runAction = <T>(
    sendResponse: (response?: any) => void,
    promise: Promise<T>
  ): void => {
    promise
      .then((response) => {
        this.sendResponseMessage(sendResponse, response)
      })
      .catch((error) => {
        this.sendErrorMessage(sendResponse, error)
      })
  }

  private readonly sendResponseMessage = (
    sendResponse: (response?: any) => void,
    response: any
  ): void => {
    const responseMessage: DelegationResponse = { response, error: undefined }
    sendResponse(responseMessage)
  }

  private readonly sendErrorMessage = (
    sendResponse: (response?: any) => void,
    error: any
  ): void => {
    const responseMessage: DelegationResponse = { response: undefined, error }
    sendResponse(responseMessage)
  }
}
